// @spec GHG-STR-089
'use client';

import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, Plus, RefreshCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useActivityData,
  useDeleteActivityData,
  useScope2ActivityDataSummary,
} from '@/features/ghg/api/use-activity-data';
import { useActivityGroups } from '@/features/ghg/api/use-activity-groups';
import { useFacilities } from '@/features/ghg/api/use-facilities';
import { ActivityDataEntryForm } from '@/features/ghg/components/ActivityDataEntryForm';
import { ScopeCalculationsEntriesCard } from '@/features/ghg/components/ScopeCalculationsEntriesCard';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import { getScope2EnergyTypeLabel } from '@/features/ghg/lib/category-labels';
import type {
  ActivityDataListFilters,
  ActivityDataSortBy,
  SortOrder,
} from '@/features/ghg/types/activity-data';
import { useTranslation } from 'react-i18next';

export const scope2SearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  facilityId: z.string().optional(),
  activityGroupId: z.string().optional(),
  dataQuality: z.enum(['measured', 'calculated', 'estimated']).optional(),
  status: z.enum(['draft', 'submitted', 'pending_approval', 'approved', 'rejected']).optional(),
  sortBy: z
    .enum([
      'createdAt',
      'estimatedCo2eKg',
      'quantity',
      'activityCategory',
      'activityType',
      'facilityName',
      'dataQualityScore',
      'status',
    ])
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  missingEmissions: z
    .preprocess((value) => {
      if (value === true || value === 'true') return true;
      if (value === false || value === 'false') return false;
      return undefined;
    }, z.boolean().optional())
    .optional(),
});

type Scope2Search = z.infer<typeof scope2SearchSchema>;

const PAGE_SIZE = 50;

export function GhgScope2CalculationsPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });
  const search = scope2SearchSchema.parse(rawSearch);
  const { facilities } = useFacilities();
  const { groups } = useActivityGroups();
  const { periodId, period, periodRange } = useGHGPagePeriod();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const currentSortBy: ActivityDataSortBy = search.sortBy ?? 'createdAt';
  const currentSortOrder: SortOrder = search.sortOrder ?? 'desc';
  const currentPage = search.page ?? 1;

  const filters = useMemo<ActivityDataListFilters>(
    () => ({
      reportingPeriodId: periodRange ? undefined : periodId,
      dateFrom: periodRange?.dateFrom,
      dateTo: periodRange?.dateTo,
      scope: 2,
      facilityId: search.facilityId,
      activityGroupId: search.activityGroupId,
      dataQuality: search.dataQuality,
      status: search.status,
      sortBy: currentSortBy,
      sortOrder: currentSortOrder,
      missingEmissions: search.missingEmissions,
      page: currentPage,
      pageSize: PAGE_SIZE,
    }),
    [
      currentPage,
      currentSortBy,
      currentSortOrder,
      search.activityGroupId,
      search.dataQuality,
      search.facilityId,
      search.missingEmissions,
      search.status,
      periodId,
      periodRange,
    ]
  );

  const summaryFilters = useMemo<ActivityDataListFilters>(
    () => ({
      reportingPeriodId: periodRange ? undefined : periodId,
      dateFrom: periodRange?.dateFrom,
      dateTo: periodRange?.dateTo,
      scope: 2,
      facilityId: search.facilityId,
      activityGroupId: search.activityGroupId,
      dataQuality: search.dataQuality,
      status: search.status,
      sortBy: currentSortBy,
      sortOrder: currentSortOrder,
      missingEmissions: search.missingEmissions,
    }),
    [
      currentSortBy,
      currentSortOrder,
      search.activityGroupId,
      search.dataQuality,
      search.facilityId,
      search.missingEmissions,
      search.status,
      periodId,
      periodRange,
    ]
  );

  const { entries, total, page, pageSize, isLoading, isError, error, refetch } =
    useActivityData(filters);
  const {
    summary,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useScope2ActivityDataSummary(summaryFilters);
  const deleteEntryMutation = useDeleteActivityData();

  const totalPages = Math.ceil(total / Math.max(pageSize, 1));

  const updateSearch = (patch: Partial<Scope2Search>) => {
    void navigate({
      to: '.',
      search: (prev) => {
        const next = { ...prev, ...patch } as Scope2Search;
        return Object.fromEntries(
          Object.entries(next).filter(([, value]) => value != null && value !== '')
        ) as Scope2Search;
      },
      replace: true,
    });
  };

  const onFilterChange = (patch: Record<string, unknown>) => {
    updateSearch({ page: 1, ...patch });
  };

  const onPageChange = (pageNumber: number) => {
    updateSearch({ page: pageNumber });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('ghgScope2CalculationsPage.heading_scope_2_emissions')}</h1>
            <p className="mt-1 text-muted-foreground">
              {t('ghgScope2.subtitle', { period: period?.name ?? t('ghgScope2.selected_reporting_period') })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
              className="gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              {t('ghgScope2.btn_refresh')}
            </Button>
            <Button
              onClick={() => {
                setCreateDialogOpen(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('ghgScope2.btn_add_entry')}
            </Button>
          </div>
        </header>

        {summaryError && (
            <Card className="border-destructive/40">
              <CardContent className="pt-6 text-sm text-destructive">
                {t('ghgScope2.error_load_summary')}
              </CardContent>
            </Card>
          )}

          {summary?.isBlocked && (
            <Card className="border-amber-400 bg-amber-50/60">
              <CardContent className="flex items-start gap-3 pt-6">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
                <div>
                  <p className="font-medium text-amber-900">{t('ghgScope2.alert_missing_estimates')}</p>
                  <p className="text-sm text-amber-800">
                    {t('ghgScope2.alert_missing_estimates_desc')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope2CalculationsPage.cardDesc_entries')}</CardDescription>
                <CardTitle className="text-2xl">
                  {summaryLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    (summary?.totals.entryCount ?? 0)
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope2CalculationsPage.cardDesc_estimated_emissions')}</CardDescription>
                <CardTitle className="text-2xl">
                  {summaryLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : summary?.totals.estimatedCo2eT != null ? (
                    `${summary.totals.estimatedCo2eT.toLocaleString(undefined, { maximumFractionDigits: 3 })} tCO2e`
                  ) : (
                    t('ghgScope2.blocked')
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope2CalculationsPage.cardDesc_location_based')}</CardDescription>
                <CardTitle className="text-2xl">
                  {summaryLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : summary?.totals.scope2LocationCo2eKg != null ? (
                    `${(summary.totals.scope2LocationCo2eKg / 1000).toLocaleString(undefined, {
                      maximumFractionDigits: 3,
                    })} tCO2e`
                  ) : (
                    t('ghgScope2.blocked')
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope2CalculationsPage.cardDesc_market_based')}</CardDescription>
                <CardTitle className="text-2xl">
                  {summaryLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : summary?.totals.scope2MarketCo2eKg != null ? (
                    `${(summary.totals.scope2MarketCo2eKg / 1000).toLocaleString(undefined, {
                      maximumFractionDigits: 3,
                    })} tCO2e`
                  ) : (
                    t('ghgScope2.blocked')
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('ghgScope2CalculationsPage.cardTitle_energy_type_breakdown')}</CardTitle>
              <CardDescription>{t('ghgScope2CalculationsPage.cardDesc_distribution_by_electricity_steam_heatin')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-5">
              {summaryLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} className="h-24 w-full" />
                  ))
                : (summary?.energyBreakdown ?? []).map((bucket) => (
                    <div key={bucket.energyType} className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">
                        {getScope2EnergyTypeLabel(bucket.energyType, t)}
                      </p>
                      <p className="mt-1 text-xl font-semibold">{bucket.entryCount}</p>
                      <p className="text-xs text-muted-foreground">
                        {bucket.estimatedCo2eKg != null
                          ? `${(bucket.estimatedCo2eKg / 1000).toLocaleString(undefined, {
                              maximumFractionDigits: 3,
                            })} tCO2e`
                          : t('ghgScope2.blocked')}
                      </p>
                    </div>
                  ))}
            </CardContent>
          </Card>

          <ScopeCalculationsEntriesCard
            scopeLabel={t('ghgScope2.scope_label')}
            periodName={period?.name}
            search={search}
            facilities={facilities}
            groups={groups}
            currentSortBy={currentSortBy}
            currentSortOrder={currentSortOrder}
            onFilterChange={onFilterChange}
            onPageChange={onPageChange}
            entries={entries}
            total={total}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            isLoading={isLoading}
            isError={isError}
            error={error}
            deletePending={deleteEntryMutation.isPending}
            onDeleteSelected={async (entryIds) => {
              await Promise.all(
                entryIds.map(async (entryId) => deleteEntryMutation.mutateAsync(entryId))
              );
              await Promise.all([refetch(), refetchSummary()]);
            }}
            onRowClick={(entry) => {
              void navigate({
                to: '/ghg/activity-data/$entryId',
                params: { entryId: entry.id },
              });
            }}
            noEntriesTitle={t('ghgScope2.no_entries_title')}
            noEntriesDescription={t('ghgScope2.no_entries_desc')}
          />
      </div>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('ghgScope2CalculationsPage.dialogTitle_add_scope_2_entry')}</DialogTitle>
            <DialogDescription>{t('ghgScope2CalculationsPage.dialogDesc_create_a_scope_2_activity_entry_for_live')}</DialogDescription>
          </DialogHeader>
          <ActivityDataEntryForm
            mode="create"
            fixedScope={2}
            defaultReportingPeriodId={periodId}
            onSuccess={(entry) => {
              setCreateDialogOpen(false);
              toast.success(t('ghgScope2CalculationsPage.success_scope_2_entry_created'));
              void navigate({
                to: '/ghg/activity-data/$entryId',
                params: { entryId: entry.id },
              });
            }}
            onCancel={() => {
              setCreateDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
